import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { LogOut, RefreshCw, X, Trash2, Filter } from "lucide-react";
import { IMAGES, USE_OPTIONS } from "../config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["New Request", "Reviewing", "Followed Up", "Temporarily Held", "Confirmed", "Declined", "Completed"];
const STATUS_COLOR = {
  "New Request": "#7C0D46", "Reviewing": "#b06", "Followed Up": "#8a5", "Temporarily Held": "#c93",
  "Confirmed": "#2a7", "Declined": "#999", "Completed": "#456",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const token = localStorage.getItem("dmedia_token");
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, confirmed: 0 });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filters, setFilters] = useState({ status: "", selected_use: "", preferred_date: "", tag: "" });

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const logout = useCallback(() => {
    localStorage.removeItem("dmedia_token");
    navigate("/admin/login");
  }, [navigate]);

  const load = useCallback(async () => {
    if (!token) { logout(); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => v && params.append(k, v));
      const [l, s] = await Promise.all([
        axios.get(`${API}/admin/leads?${params.toString()}`, auth),
        axios.get(`${API}/admin/leads/stats`, auth),
      ]);
      setLeads(l.data);
      setStats(s.data);
    } catch (err) {
      if (err.response?.status === 401) logout();
    } finally {
      setLoading(false);
    }
  }, [filters, token]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const updateLead = async (id, patch) => {
    try {
      const { data } = await axios.patch(`${API}/admin/leads/${id}`, patch, auth);
      setLeads((ls) => ls.map((l) => (l.id === id ? data : l)));
      if (selected?.id === id) setSelected(data);
      load();
    } catch (err) { if (err.response?.status === 401) logout(); }
  };

  const removeLead = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    await axios.delete(`${API}/admin/leads/${id}`, auth);
    setSelected(null);
    load();
  };

  return (
    <div className="min-h-screen bg-offwhite text-charcoal">
      <header className="bg-charcoal text-offwhite px-5 md:px-8 py-4 flex items-center justify-between sticky top-0 z-30">
        <div className="flex items-center gap-4">
          <img src={IMAGES.logo} alt="DMEDIA CLUB" className="h-7 w-auto brightness-0 invert" />
          <span className="text-xs uppercase tracking-widest text-offwhite/50 hidden sm:inline">Lead Dashboard</span>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={load} data-testid="refresh-btn" className="p-2 hover:text-plum transition-colors" title="Refresh">
            <RefreshCw size={18} strokeWidth={1.6} className={loading ? "animate-spin" : ""} />
          </button>
          <button onClick={logout} data-testid="logout-btn" className="inline-flex items-center gap-2 text-sm hover:text-plum transition-colors">
            <LogOut size={16} strokeWidth={1.6} /> Logout
          </button>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-8">
        {/* stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          {[["Total Requests", stats.total], ["New", stats.new], ["Confirmed", stats.confirmed]].map(([k, v]) => (
            <div key={k} className="bg-white border border-charcoal/10 p-6">
              <p className="text-xs uppercase tracking-widest text-charcoal/50">{k}</p>
              <p className="display text-4xl md:text-5xl mt-2 text-plum">{v}</p>
            </div>
          ))}
        </div>

        {/* filters */}
        <div className="bg-white border border-charcoal/10 p-4 mb-6 flex flex-wrap items-center gap-3" data-testid="filters">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-charcoal/50"><Filter size={14} /> Filter</span>
          <select className="border border-charcoal/20 px-3 py-2 text-sm bg-transparent" value={filters.status}
            onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))} data-testid="filter-status">
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select className="border border-charcoal/20 px-3 py-2 text-sm bg-transparent" value={filters.selected_use}
            onChange={(e) => setFilters((f) => ({ ...f, selected_use: e.target.value }))} data-testid="filter-use">
            <option value="">All uses</option>
            {USE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select className="border border-charcoal/20 px-3 py-2 text-sm bg-transparent" value={filters.tag}
            onChange={(e) => setFilters((f) => ({ ...f, tag: e.target.value }))} data-testid="filter-tag">
            <option value="">All sources</option>
            <option value="opening_offer">Opening Offer</option>
            <option value="recurring_package">Recurring Package</option>
            <option value="notch_coffee">Notch Coffee interest</option>
          </select>
          <input type="date" className="border border-charcoal/20 px-3 py-2 text-sm bg-transparent" value={filters.preferred_date}
            onChange={(e) => setFilters((f) => ({ ...f, preferred_date: e.target.value }))} data-testid="filter-date" />
          {(filters.status || filters.selected_use || filters.tag || filters.preferred_date) && (
            <button onClick={() => setFilters({ status: "", selected_use: "", preferred_date: "", tag: "" })}
              className="text-sm text-plum hover:underline">Clear</button>
          )}
        </div>

        {/* table */}
        <div className="bg-white border border-charcoal/10 overflow-x-auto" data-testid="leads-table">
          <table className="w-full text-sm">
            <thead className="bg-charcoal text-offwhite">
              <tr className="text-left">
                {["Date", "Name", "Use", "Preferred Date", "Duration", "Status", ""].map((h) => (
                  <th key={h} className="px-4 py-3 font-medium text-xs uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {leads.length === 0 && !loading && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-charcoal/50">No leads yet.</td></tr>
              )}
              {leads.map((l) => (
                <tr key={l.id} className="border-b border-charcoal/8 hover:bg-offwhite cursor-pointer" onClick={() => setSelected(l)} data-testid={`lead-row-${l.id}`}>
                  <td className="px-4 py-3 text-charcoal/60 whitespace-nowrap">{(l.created_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-3 font-medium">{l.full_name}
                    {l.tags?.length > 0 && <span className="ml-2 text-[10px] uppercase text-plum">{l.tags.join(", ")}</span>}
                  </td>
                  <td className="px-4 py-3 text-charcoal/70">{l.selected_use}</td>
                  <td className="px-4 py-3 text-charcoal/70 whitespace-nowrap">{l.preferred_date || "—"}</td>
                  <td className="px-4 py-3 text-charcoal/70">{l.duration || "—"}</td>
                  <td className="px-4 py-3">
                    <span className="inline-block px-2.5 py-1 text-xs text-white" style={{ backgroundColor: STATUS_COLOR[l.status] || "#7C0D46" }}>{l.status}</span>
                  </td>
                  <td className="px-4 py-3 text-plum text-xs">View</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-40 flex justify-end" data-testid="lead-detail">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSelected(null)} />
          <div className="relative w-full max-w-lg bg-white h-full overflow-y-auto">
            <div className="sticky top-0 bg-charcoal text-offwhite px-6 py-4 flex items-center justify-between">
              <h3 className="font-serif text-xl">{selected.full_name}</h3>
              <button onClick={() => setSelected(null)} data-testid="detail-close"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-5">
              <div className="flex flex-wrap gap-2">
                {STATUSES.map((s) => (
                  <button key={s} onClick={() => updateLead(selected.id, { status: s })}
                    data-testid={`status-btn-${s.replace(/\s/g, "-")}`}
                    className={`px-3 py-1.5 text-xs border transition-colors ${selected.status === s ? "text-white border-transparent" : "border-charcoal/20 text-charcoal/70"}`}
                    style={selected.status === s ? { backgroundColor: STATUS_COLOR[s] } : {}}>
                    {s}
                  </button>
                ))}
              </div>

              {[
                ["Selected use", selected.selected_use],
                ["Business", selected.business],
                ["Email", selected.email],
                ["Phone", selected.phone],
                ["Instagram / Website", selected.instagram_website],
                ["Description", selected.activity_description],
                ["Preferred date", selected.preferred_date],
                ["Alternative date", selected.alternative_date],
                ["Start time", selected.preferred_start_time],
                ["Start time flexible", selected.start_time_flexible],
                ["Duration", selected.duration],
                ["Expected attendees", selected.expected_attendees],
                ["Configuration", selected.configuration],
                ["Paid attendance", selected.paid_attendance],
                ["Outside vendors", selected.outside_vendors],
                ["Notch Coffee interest", selected.notch_coffee_interest],
                ["Special requirements", selected.special_requirements],
                ["Tags", (selected.tags || []).join(", ")],
                ["CTA source", selected.cta_source],
              ].map(([k, v]) => (
                <div key={k} className="grid grid-cols-3 gap-3 text-sm">
                  <span className="text-charcoal/50 uppercase text-xs tracking-wider">{k}</span>
                  <span className="col-span-2 text-charcoal/85 break-words">{v || "—"}</span>
                </div>
              ))}

              <div>
                <label className="block text-xs uppercase tracking-widest text-charcoal/50 mb-2">Internal notes</label>
                <textarea rows={4} defaultValue={selected.internal_notes} data-testid="internal-notes"
                  onBlur={(e) => updateLead(selected.id, { internal_notes: e.target.value })}
                  className="w-full border border-charcoal/20 px-3 py-2 text-sm focus:border-plum focus:outline-none" />
                <p className="text-xs text-charcoal/40 mt-1">Notes save automatically when you click away.</p>
              </div>

              <a href={`tel:${(selected.phone || "").replace(/[^\d+]/g, "")}`} className="inline-block text-sm text-plum">Call {selected.phone}</a>

              <button onClick={() => removeLead(selected.id)} data-testid="delete-lead"
                className="mt-4 inline-flex items-center gap-2 text-sm text-charcoal/50 hover:text-plum transition-colors">
                <Trash2 size={15} /> Delete lead
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
